import { useContext } from 'react';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

import { ProposalDto} from '../api/api.ts'
import { MetadataContext } from '../metadataProvider.tsx'
import { InputAutocomplete, SearchFunctions, SearchResult, SelectingStatus } from './inputAutocomplete.tsx'
import { highlightSearchTermsInString } from "../utils.tsx";
import './proposalAutocomplete.css';


interface ProposalSearchFunctions {
  itemSelected: (item: ProposalDto) => SelectingStatus;
  selectedNone: () => SelectingStatus;
}


// Settings passed in with the React component
interface ProposalAutocompleteParameters {
  allowedProposalSlugs?: string[] | null;
  selectedItem: ProposalDto | null;
  searchFunctions: ProposalSearchFunctions;
  isReadOnly?: boolean;
  greenWhenValid?: boolean;
  canSelectNone?: boolean;
  showClearButton?: boolean;
  triggerFocus?: boolean;
  resetOnDefocus?: boolean;
  inputSize?: string;
  iconSize?: SizeProp;
  placeholder?: string;
}


function ProposalAutocomplete(settings:ProposalAutocompleteParameters) {

  const metadataContext = useContext(MetadataContext);

  async function searchProposals(searchString: string): Promise<SearchResult<ProposalDto>> {
    const searchStringLower = searchString.toLowerCase();
    // If allowedProposalSlugs is an array, we use it even if it's empty.
    const idsToTest = Array.isArray(settings.allowedProposalSlugs) ? settings.allowedProposalSlugs : metadataContext.proposals.slugsInDisplayOrder;
    const toTest = idsToTest.map((t) => metadataContext.proposals.bySlug.get(t)!);
    const matches = toTest.filter((s) =>
                      s.name.toLowerCase().includes(searchStringLower) ||
                      (s.description && s.description.toLowerCase().includes(searchStringLower)));
    return { matches: matches };
  };

  function renderMatch(item: ProposalDto, searchString: string): React.JSX.Element {
    return (<>
              <td>{ highlightSearchTermsInString(item.name, [searchString]) }</td>
              <td>{ highlightSearchTermsInString(item.description || "", [searchString]) }</td>
            </>);
  }

  const proposalSearchFunctions: SearchFunctions<ProposalDto> = {
    itemToString: (item) => { return item.name },
    itemsRetriever: searchProposals,
    itemSelected: settings.searchFunctions.itemSelected,
    selectedNone: settings.searchFunctions.selectedNone,
    renderMatchedItem: renderMatch
  };

  return InputAutocomplete<ProposalDto>({
      value: settings.selectedItem ? settings.selectedItem.name : "",
      selectedItem: settings.selectedItem,
      searchFunctions: proposalSearchFunctions,
      addedClass: "proposal",
      inputSize: settings.inputSize,
      iconSize: settings.iconSize,
      placeholder: settings.placeholder || "Select a Proposal",
      isReadOnly: settings.isReadOnly,
      greenWhenValid: settings.greenWhenValid,
      canSelectNone: settings.canSelectNone,
      showClearButton: settings.showClearButton,
      resetOnDefocus: settings.resetOnDefocus,
      triggerFocus: settings.triggerFocus,
      showAllByDefault: true,
      renderResultsAsTable: true
    });
}

export { ProposalAutocomplete }
export type { ProposalSearchFunctions }
